import React, { useMemo, useState } from 'react';
import { Supplier } from '../types';
import { usePos } from '../context/PosContext';
import './Products.css';

// Supplier Master (supplier_list_layout.xml + activity_supplier_detail_activity.xml):
// two-pane list + form. Suppliers feed Goods Received / Order Request.
const blank = {
  name: '',
  phone: '',
  shortName: '',
  searchCode: '',
  altNo: '',
  email: '',
  gstin: '',
  address: '',
  extra: '',
};

const Suppliers: React.FC = () => {
  const { suppliers, addSupplier, updateSupplier, deleteSupplier } = usePos();
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [listSearch, setListSearch] = useState('');
  const [f, setF] = useState({ ...blank });

  const set = (k: keyof typeof blank, v: string) => setF((prev) => ({ ...prev, [k]: v }));

  const filtered = useMemo(() => {
    const t = listSearch.trim().toLowerCase();
    if (!t) return suppliers;
    return suppliers.filter(
      (s) =>
        s.name.toLowerCase().includes(t) ||
        s.shortName?.toLowerCase().includes(t) ||
        s.searchCode?.toLowerCase().includes(t) ||
        s.phone?.includes(listSearch)
    );
  }, [suppliers, listSearch]);

  const reset = () => {
    setF({ ...blank });
    setEditing(null);
  };

  const buildData = (): Omit<Supplier, 'id'> => ({
    name: f.name,
    phone: f.phone || undefined,
    shortName: f.shortName || undefined,
    searchCode: f.searchCode || undefined,
    altNo: f.altNo || undefined,
    email: f.email || undefined,
    gstin: f.gstin || undefined,
    address: f.address || undefined,
    extra: f.extra || undefined,
  });

  const save = () => {
    if (!f.name || !f.phone) {
      alert('Name and Mobile are required.');
      return;
    }
    if (editing) updateSupplier({ ...editing, ...buildData() });
    else addSupplier(buildData());
    reset();
  };

  const edit = (s: Supplier) => {
    setEditing(s);
    setF({
      name: s.name,
      phone: s.phone ?? '',
      shortName: s.shortName ?? '',
      searchCode: s.searchCode ?? '',
      altNo: s.altNo ?? '',
      email: s.email ?? '',
      gstin: s.gstin ?? '',
      address: s.address ?? '',
      extra: s.extra ?? '',
    });
  };

  const remove = () => {
    if (editing && window.confirm('Delete this supplier?')) {
      deleteSupplier(editing.id);
      reset();
    }
  };

  const lbl = (t: string) => <label className="pf-lbl">{t}</label>;

  return (
    <div className="pm-page">
      {/* list */}
      <div className="pm-list card-rounded">
        <input
          className="pm-list-search"
          placeholder="Search by Name, Short Name or Search Code"
          value={listSearch}
          onChange={(e) => setListSearch(e.target.value)}
        />
        <div className="pm-list-scroll">
          {filtered.map((s) => (
            <button key={s.id} className={`pm-list-item ${editing?.id === s.id ? 'sel' : ''}`} onClick={() => edit(s)}>
              <span className="pm-li-name">{s.name}</span>
              <span className="pm-li-meta">
                {s.phone || '—'}
                {s.shortName ? ` · ${s.shortName}` : ''}
              </span>
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="muted" style={{ padding: 16 }}>
              No suppliers.
            </p>
          )}
        </div>
      </div>

      {/* form */}
      <div className="pm-form card-rounded">
        <div className="pf-top">
          <span style={{ fontWeight: 700, color: 'var(--color-primary-dark)' }}>
            {editing ? 'Edit Supplier' : 'New Supplier'}
          </span>
          <div className="pf-top-btns">
            {editing && (
              <button className="pf-btn-orange" onClick={reset}>
                New
              </button>
            )}
            {editing && (
              <button className="pf-btn-orange" onClick={remove}>
                Delete
              </button>
            )}
            <button className="pf-btn-orange" onClick={save}>
              {editing ? 'Update' : 'Save'}
            </button>
          </div>
        </div>

        <div className="pf-scroll">
          <div className="pf-row r2">
            <div>{lbl('Name *')}<input value={f.name} onChange={(e) => set('name', e.target.value)} /></div>
            <div>{lbl('Mobile *')}<input value={f.phone} onChange={(e) => set('phone', e.target.value)} /></div>
          </div>
          <div className="pf-row r2">
            <div>{lbl('Short Name')}<input value={f.shortName} onChange={(e) => set('shortName', e.target.value)} /></div>
            <div>{lbl('Search Code')}<input value={f.searchCode} onChange={(e) => set('searchCode', e.target.value)} /></div>
          </div>
          <div className="pf-row r2">
            <div>{lbl('Alternate Numbers')}<input value={f.altNo} onChange={(e) => set('altNo', e.target.value)} /></div>
            <div>{lbl('Email')}<input value={f.email} onChange={(e) => set('email', e.target.value)} /></div>
          </div>
          <div className="pf-row r1">
            <div>{lbl('GST IN')}<input value={f.gstin} onChange={(e) => set('gstin', e.target.value)} /></div>
          </div>
          <div className="pf-row r1">
            <div>{lbl('Address')}<textarea value={f.address} onChange={(e) => set('address', e.target.value)} /></div>
          </div>
          <div className="pf-row r1">
            <div>{lbl('Extra')}<input value={f.extra} onChange={(e) => set('extra', e.target.value)} /></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Suppliers;
